<template>
  <div class="intercom-topbar">
    <!-- Left Side -->
    <div class="intercom-topbar-left">
      <!-- Logo -->
      <router-link to="/" class="intercom-topbar-logo">
        <img src="/images/logo.png" alt="OnePlaceHQ" class="intercom-topbar-logo-image" />
        <div class="intercom-topbar-product-label">{{ businessName || productName }}</div>
      </router-link>

      <!-- Search -->
      <div v-if="showSearch" class="intercom-topbar-search" ref="searchContainerRef">
        <svg
          class="intercom-topbar-search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="intercom-topbar-search-input"
          :placeholder="searchPlaceholder"
          @input="handleSearchInput"
          @focus="showDropdown = searchQuery.length >= 2"
          @keydown.escape="closeDropdown"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
          @keydown.enter.prevent="selectFocused"
        />

        <!-- Search Results Dropdown -->
        <div v-if="showDropdown && searchQuery.length >= 2" class="search-dropdown">
          <div v-if="isSearching" class="search-dropdown-loading">
            Searching...
          </div>
          <template v-else-if="hasResults">
            <!-- Customers Section -->
            <div v-if="searchResults.customers?.length" class="search-dropdown-section">
              <div class="search-dropdown-header">Customers</div>
              <div
                v-for="(customer, index) in searchResults.customers"
                :key="'c-' + customer.id"
                class="search-dropdown-item"
                :class="{ 'is-focused': index === focusedIndex }"
                @click="handleCustomerClick(customer)"
              >
                <div class="search-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div class="search-item-content">
                  <div class="search-item-title">{{ customer.name }}</div>
                  <div class="search-item-subtitle">{{ customer.phone }}</div>
                </div>
              </div>
            </div>

            <!-- Pledges Section -->
            <div v-if="searchResults.pledges?.length" class="search-dropdown-section">
              <div class="search-dropdown-header">Pledges</div>
              <div
                v-for="(pledge, index) in searchResults.pledges"
                :key="'p-' + pledge.id"
                class="search-dropdown-item"
                :class="{ 'is-focused': (searchResults.customers?.length || 0) + index === focusedIndex }"
                @click="handlePledgeClick(pledge)"
              >
                <div class="search-item-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <div class="search-item-content">
                  <div class="search-item-title">{{ pledge.pledge_number }}</div>
                  <div class="search-item-subtitle">{{ pledge.customer_name }}</div>
                </div>
                <span :class="['search-item-badge', 'badge-' + pledge.status]">{{ pledge.status }}</span>
              </div>
            </div>
          </template>
          <div v-else class="search-dropdown-empty">
            No results found
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side -->
    <div class="intercom-topbar-right">
      <!-- Keyboard shortcut hint -->
      <button class="intercom-topbar-shortcut-hint" @click="$emit('shortcut-help')" title="Press G to navigate quickly">
        <kbd>G</kbd>
        <span>Navigate</span>
      </button>

      <!-- Live Clock -->
      <div class="intercom-topbar-clock">
        <span class="clock-date">{{ formattedDate }}</span>
        <span class="clock-separator">•</span>
        <span class="clock-time">{{ formattedTime }}</span>
      </div>

      <!-- Slot for custom content before notifications (e.g., metal rates) -->
      <slot name="before-notifications" />

      <!-- Notifications -->
      <button
        v-if="showNotifications"
        class="intercom-topbar-btn"
        aria-label="Notifications"
        @click="handleNotifications"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <span v-if="notificationCount > 0" class="intercom-topbar-notification-dot" />
      </button>

      <!-- Settings -->
      <button
        v-if="showSettings"
        class="intercom-topbar-btn"
        aria-label="Settings"
        @click="handleSettings"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>

      <!-- User Avatar with Dropdown -->
      <IntercomDropdown align="right">
        <template #trigger>
          <div class="intercom-topbar-avatar" :title="user?.username || 'User'">
            {{ userInitial }}
          </div>
        </template>

        <!-- Dropdown Menu Content -->
        <div class="user-dropdown-menu">
          <div class="user-dropdown-header">
            <div class="user-dropdown-name">{{ displayName }}</div>
            <div class="user-dropdown-role">{{ user?.role || 'Staff' }}</div>
          </div>
          <div class="user-dropdown-divider"></div>
          <button class="user-dropdown-item" @click="handleLogout">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </IntercomDropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import IntercomDropdown from './IntercomDropdown.vue'

const props = defineProps({
  /**
   * Application name
   */
  appName: {
    type: String,
    default: 'OnePlaceHQ',
  },
  /**
   * Product/module name
   */
  productName: {
    type: String,
    default: 'Billing',
  },
  /**
   * Business name (overrides productName if provided)
   */
  businessName: {
    type: String,
    default: '',
  },
  /**
   * Show search bar
   */
  showSearch: {
    type: Boolean,
    default: true,
  },
  /**
   * Search placeholder
   */
  searchPlaceholder: {
    type: String,
    default: 'Search...',
  },
  /**
   * Show notifications button
   */
  showNotifications: {
    type: Boolean,
    default: true,
  },
  /**
   * Notification count
   */
  notificationCount: {
    type: Number,
    default: 0,
  },
  /**
   * Show settings button
   */
  showSettings: {
    type: Boolean,
    default: true,
  },
  /**
   * User object (Profile)
   */
  user: {
    type: Object,
    default: null,
  },
  /**
   * Global user object (contains name, phone)
   */
  globalUser: {
    type: Object,
    default: null,
  },
  /**
   * Search function - async function that takes query and returns results
   * Expected return: { customers: [...], pledges: [...] }
   */
  searchFn: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['search', 'notifications', 'settings', 'user-menu', 'logout', 'select-customer', 'select-pledge', 'shortcut-help'])

// Refs
const searchQuery = ref('')
const searchContainerRef = ref(null)
const showDropdown = ref(false)
const isSearching = ref(false)
const searchResults = ref({ customers: [], pledges: [] })
const focusedIndex = ref(-1)
let searchTimeout = null

// Live clock
const currentTime = ref(new Date())
let clockInterval = null

// Computed
const formattedDate = computed(() => {
  return currentTime.value.toLocaleDateString('en-IN', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })
})

const formattedTime = computed(() => {
  return currentTime.value.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
})

const userInitial = computed(() => {
  if (props.globalUser?.name) {
    return props.globalUser.name.charAt(0).toUpperCase()
  }
  // Skip phone-like usernames (starting with + or numeric)
  if (props.user?.username && !props.user.username.startsWith('+') && !/^\d/.test(props.user.username)) {
    return props.user.username.charAt(0).toUpperCase()
  }
  // Try user.name as another fallback
  if (props.user?.name) {
    return props.user.name.charAt(0).toUpperCase()
  }
  return 'U'
})

const displayName = computed(() => {
  if (props.globalUser?.name && props.globalUser?.phone) {
    return `${props.globalUser.name} (${props.globalUser.phone})`
  }
  return props.user?.username || 'User'
})

const hasResults = computed(() => {
  return (searchResults.value.customers?.length > 0) || (searchResults.value.pledges?.length > 0)
})

// Flatten results for keyboard navigation
const allResults = computed(() => {
  const items = []
  searchResults.value.customers?.forEach(c => items.push({ type: 'customer', data: c }))
  searchResults.value.pledges?.forEach(p => items.push({ type: 'pledge', data: p }))
  return items
})

// Search handlers
const handleSearchInput = async () => {
  const query = searchQuery.value.trim()

  if (query.length < 2) {
    showDropdown.value = false
    searchResults.value = { customers: [], pledges: [] }
    return
  }

  // Debounce search
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(async () => {
    if (!props.searchFn) {
      emit('search', query)
      return
    }

    isSearching.value = true
    showDropdown.value = true

    try {
      const result = await props.searchFn(query)
      searchResults.value = {
        customers: result.customers || [],
        pledges: result.pledges || [],
      }
      focusedIndex.value = -1  // Reset focus on new results
    } catch (error) {
      console.error('Search error:', error)
      searchResults.value = { customers: [], pledges: [] }
    } finally {
      isSearching.value = false
    }
  }, 300)
}

const handleCustomerClick = (customer) => {
  emit('select-customer', customer)
  closeDropdown()
}

const handlePledgeClick = (pledge) => {
  emit('select-pledge', pledge)
  closeDropdown()
}

const closeDropdown = () => {
  showDropdown.value = false
  searchQuery.value = ''
  searchResults.value = { customers: [], pledges: [] }
  focusedIndex.value = -1
}

// Keyboard navigation handlers
const navigateDown = () => {
  if (!showDropdown.value || !allResults.value.length) return
  focusedIndex.value = Math.min(focusedIndex.value + 1, allResults.value.length - 1)
}

const navigateUp = () => {
  if (!showDropdown.value || !allResults.value.length) return
  focusedIndex.value = Math.max(focusedIndex.value - 1, 0)
}

const selectFocused = () => {
  if (focusedIndex.value < 0 || !allResults.value.length) return
  const item = allResults.value[focusedIndex.value]
  if (item.type === 'customer') {
    handleCustomerClick(item.data)
  } else {
    handlePledgeClick(item.data)
  }
}

// Click outside handler
const handleClickOutside = (event) => {
  if (searchContainerRef.value && !searchContainerRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Start clock interval - update every minute
  clockInterval = setInterval(() => {
    currentTime.value = new Date()
  }, 60000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  if (clockInterval) {
    clearInterval(clockInterval)
  }
})

const handleNotifications = () => {
  emit('notifications')
}

const handleSettings = () => {
  emit('settings')
}

const handleUserMenu = () => {
  emit('user-menu')
}

const handleLogout = () => {
  emit('logout')
}
</script>

<style scoped>
/* ========================================
   INTERCOM TOPBAR BASE
   ======================================== */
.intercom-topbar {
  height: var(--topbar-height);
  background: var(--color-white);
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-6);
}

/* ========================================
   LEFT SIDE
   ======================================== */
.intercom-topbar-left {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}

.intercom-topbar-logo {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
  transition: opacity var(--transition-fast);
}

.intercom-topbar-logo:hover {
  opacity: 0.8;
}

.intercom-topbar-logo-image {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.intercom-topbar-product-label {
  font-size: var(--font-size-base);
  color: #7c3aed;
  font-weight: 700;
  padding-left: var(--space-3);
  border-left: 2px solid #7c3aed;
}

/* ========================================
   SEARCH
   ======================================== */
.intercom-topbar-search {
  position: relative;
  width: 400px;
}

.intercom-topbar-search-icon {
  position: absolute;
  left: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-gray-500);
  pointer-events: none;
}

.intercom-topbar-search-input {
  width: 100%;
  padding: var(--space-2) var(--space-4) var(--space-2) var(--space-10);
  background: var(--color-gray-100);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  transition: all var(--transition-base);
  outline: none;
}

.intercom-topbar-search-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: var(--shadow-focus);
}

.intercom-topbar-search-input::placeholder {
  color: var(--color-gray-500);
}

/* ========================================
   RIGHT SIDE
   ======================================== */
.intercom-topbar-right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* ========================================
   KEYBOARD SHORTCUT HINT
   ======================================== */
.intercom-topbar-shortcut-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: none;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 11px;
  color: var(--color-gray-500);
  transition: all 0.15s;
}
.intercom-topbar-shortcut-hint:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-300);
  color: var(--color-gray-700);
}
.intercom-topbar-shortcut-hint kbd {
  background: var(--color-gray-100);
  border: 1px solid var(--color-gray-300);
  border-radius: 3px;
  padding: 0 4px;
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-gray-700);
  line-height: 1.4;
}
.intercom-topbar-shortcut-hint span {
  font-weight: 500;
}

/* ========================================
   LIVE CLOCK
   ======================================== */
.intercom-topbar-clock {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  margin-right: var(--space-3);
}

.clock-date {
  font-weight: var(--font-weight-medium);
}

.clock-separator {
  color: var(--color-gray-400);
}

.clock-time {
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.intercom-topbar-btn {
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-gray-500);
  transition: all var(--transition-fast);
  position: relative;
}

.intercom-topbar-btn:hover {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

.intercom-topbar-notification-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: var(--color-error);
  border: 2px solid var(--color-white);
  border-radius: 50%;
}

/* ========================================
   USER AVATAR
   ======================================== */
.intercom-topbar-user {
  margin-left: var(--space-2);
}

.intercom-topbar-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-success) 0%, #17a347 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: var(--font-size-base);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.intercom-topbar-avatar:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
}

/* ========================================
   USER DROPDOWN MENU
   ======================================== */
.user-dropdown-menu {
  min-width: 220px;
}

.user-dropdown-header {
  padding: var(--space-3) var(--space-4);
}

.user-dropdown-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin-bottom: 2px;
}

.user-dropdown-role {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  text-transform: capitalize;
}

.user-dropdown-divider {
  height: 1px;
  background: var(--color-gray-200);
  margin: var(--space-2) 0;
}

.user-dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: left;
}

.user-dropdown-item:hover {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

.user-dropdown-item svg {
  flex-shrink: 0;
}

/* ========================================
   SEARCH DROPDOWN
   ======================================== */
.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: var(--space-2);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
}

.search-dropdown-loading,
.search-dropdown-empty {
  padding: var(--space-4);
  text-align: center;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.search-dropdown-section {
  padding: var(--space-2) 0;
}

.search-dropdown-section:not(:last-child) {
  border-bottom: 1px solid var(--color-gray-100);
}

.search-dropdown-header {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.search-dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.search-dropdown-item:hover {
  background: var(--color-gray-50);
}

.search-dropdown-item.is-focused {
  background: var(--color-blue-50, #eff6ff);
  outline: 2px solid var(--color-blue, #3b82f6);
  outline-offset: -2px;
}

.search-item-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  color: var(--color-gray-500);
  flex-shrink: 0;
}

.search-item-content {
  flex: 1;
  min-width: 0;
}

.search-item-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-item-subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-item-badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: 2px 8px;
  border-radius: var(--radius-full);
  text-transform: capitalize;
  flex-shrink: 0;
}

.badge-active {
  background: var(--color-success-bg, #dcfce7);
  color: var(--color-success, #16a34a);
}

.badge-redeemed {
  background: var(--color-gray-100);
  color: var(--color-gray-600);
}

.badge-forfeited {
  background: var(--color-error-bg, #fee2e2);
  color: var(--color-error, #dc2626);
}

/* ========================================
   MOBILE RESPONSIVE
   ======================================== */
@media (max-width: 768px) {
  .intercom-topbar-search {
    display: none;
  }

  .intercom-topbar {
    padding: 0 var(--space-4);
  }

  .intercom-topbar-product-label {
    display: none;
  }

  .intercom-topbar-clock {
    display: none;
  }
}
</style>
